import {
  ButtonPrimary,
  ButtonPseudo,
  ChangeLogs,
  PermissionDenied,
  PermissionEnum,
  SecuritySidepanelLocksList,
  SecuritySidepanelLogsList,
  SidePanel,
  SidePanelType,
  Tabs,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import SystemLogs from '../../../../components/new-application/common/system-logs';
import { clearDetailsPanelState } from '../../store/slices';
import {
  locksColumns,
  locksColumnsLabels,
  locksFiltersSchema,
  locksSchema,
} from '../variables/locks';
import {
  auditLogsColumns,
  auditLogsColumnsLabels,
  auditLogsFiltersSchema,
  auditLogsSchema,
} from '../variables/audit-logs';
import { NewApplicationTopSidepanelsTypes } from '../types';
import ClientDetails from '../../../../components/new-application/common/client-details';
import ManualRiskScoreForm from '../../../../components/new-application/common/manual-risk-score-form';

import cn from '../styles.module.css';

const NewApplicationTopSidepanels = ({
  changeLogsPanelIsOpen,
  globalError,
  setChangeLogsPanelIsOpen,
  activePermissions,
  activeTab,
  setActiveTab,
  systemLogs,
  applicationId,
  changeLogsIsLoading,
  changeLogs,
  changeLogsPagination,
  changeLogsIsFetching,
  getChangeLogsAction,
  userSidepanelVisible,
  setUserSidepanelVisible,
  setUserDetailsActiveTab,
  userDetailsActiveTab,
  applicationType,
  getLocks,
  newApplicationInfo,
  activeStatus,
  updateClientInProgress,
  onUpdateClientStatus,
  recoveryInProgress,
  onResetPassword,
  getListAuditLogs,
  auditLogsIsFetching,
  auditLogsIsLoading,
  employees,
  setLockId,
  setLockFormVisible,
  locksIsFetching,
  locksIsLoading,
  manualScoreSidepanelVisible,
  setManualScoreSidepanelVisible,
  setTotalRiskScore,
  setTotalRiskScoreLevel,
  setManualRiskScoreError,
  manualRiskScoreError,
  createRiskScoreIsLoading,
  createApplicationManualRiskScoreAction,
  totalRiskScoreLevel,
  riskScoreRanges,
  totalRiskScore,
}: NewApplicationTopSidepanelsTypes) => {
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const { auditLogs, auditLogsPagination, locks, locksPagination, activeLock } =
    useAppSelector((state) => state.newApplications.list);

  const dispatch = useAppDispatch();

  let systemLogContent = (
    <div className={cn.emptyChangeLogs}>No system logs yet</div>
  );

  if (systemLogs && systemLogs.length) {
    systemLogContent = <SystemLogs systemLogs={systemLogs} />;
  }

  return (
    <>
      <SidePanel
        isOpen={changeLogsPanelIsOpen}
        onClose={() => {
          setChangeLogsPanelIsOpen(false);
        }}
        errorSidepanel={globalError}
        title="Application change logs"
        sidepanelExpandedKey="application-change-logs"
        type={SidePanelType.Accounts}
        footer={
          <div>
            <ButtonPseudo
              text="Close"
              onClick={() => setChangeLogsPanelIsOpen(false)}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <Tabs
              tabs={[
                {
                  name: 'System log',
                  value: 'system',
                },
                {
                  name: 'Change log',
                  value: 'change',
                },
              ]}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as 'system' | 'change')}
              wrapperClassName={cn.applicationLogsTabs}
            />
          )}
          {activeTab === 'system' && systemLogContent}
          {activeTab === 'change' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="APPLICATION"
                  entityId={applicationId as string}
                  changeLogsIsLoading={changeLogsIsLoading}
                  changeLogs={changeLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={changeLogsPagination}
                  changeLogsIsFetching={changeLogsIsFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
        </>
      </SidePanel>
      <SidePanel
        title="Client details"
        sidepanelExpandedKey="application-client-details"
        isOpen={userSidepanelVisible}
        type={SidePanelType.Accounts}
        onClose={() => {
          setUserSidepanelVisible(false);
          setUserDetailsActiveTab('general');
          dispatch(clearDetailsPanelState());
        }}
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setUserSidepanelVisible(false);
              setUserDetailsActiveTab('general');
              dispatch(clearDetailsPanelState());
            }}
            bold
          />
        }
        closeOnClickOutside
      >
        <>
          <Tabs
            tabs={[
              {
                name: 'Details',
                value: 'general',
              },
              {
                name: 'Locks',
                value: 'locks',
              },
              {
                name: 'Audit Logs',
                value: 'logs',
              },
            ]}
            activeTab={userDetailsActiveTab}
            wrapperClassName={cn.clientTabs}
            onClick={(tab) => {
              setUserDetailsActiveTab(tab as 'general' | 'locks' | 'logs');
            }}
          />
          {userDetailsActiveTab === 'general' && (
            <ClientDetails
              setUserDetailsActiveTab={setUserDetailsActiveTab}
              activeLock={activeLock}
              applicationType={applicationType}
              applicationId={applicationId}
              locks={locks}
              getLocks={getLocks}
              newApplicationInfo={newApplicationInfo}
              activeStatus={activeStatus}
              updateClientInProgress={updateClientInProgress}
              onUpdateClientStatus={onUpdateClientStatus}
              activePermissions={activePermissions}
              recoveryInProgress={recoveryInProgress}
              onResetPassword={onResetPassword}
            />
          )}
          {userDetailsActiveTab === 'logs' && (
            <div className={cn.clientsDetailsLogs}>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <SecuritySidepanelLogsList
                  auditLogs={auditLogs}
                  auditLogsPagination={auditLogsPagination}
                  panelIsOpen={userSidepanelVisible}
                  activePermissions={activePermissions}
                  userId={newApplicationInfo?.user?.id}
                  getList={getListAuditLogs}
                  schema={auditLogsSchema}
                  filtersSchema={auditLogsFiltersSchema}
                  isFetching={auditLogsIsFetching}
                  isLoading={auditLogsIsLoading}
                  columns={auditLogsColumns}
                  columnsLabels={auditLogsColumnsLabels}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </div>
          )}
          {userDetailsActiveTab === 'locks' && (
            <div className={cn.clientsDetailsLogs}>
              {activePermissions?.includes(PermissionEnum.LOCKS_LIST) && (
                <SecuritySidepanelLocksList
                  users={employees}
                  locks={locks}
                  locksPagination={locksPagination}
                  userId={newApplicationInfo?.user?.id}
                  getList={getLocks}
                  schema={locksSchema}
                  filtersSchema={locksFiltersSchema}
                  setLockId={setLockId}
                  onBlockUserClick={() => {
                    setUserSidepanelVisible(false);
                    setLockFormVisible(true);
                  }}
                  isFetching={locksIsFetching}
                  activePermissions={activePermissions}
                  panelIsOpen={userSidepanelVisible}
                  isLoading={locksIsLoading}
                  columns={locksColumns}
                  columnsLabels={locksColumnsLabels}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.LOCKS_LIST) && (
                <PermissionDenied />
              )}
            </div>
          )}
        </>
      </SidePanel>

      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={manualScoreSidepanelVisible}
        onClose={() => {
          setManualScoreSidepanelVisible(false);
          setTotalRiskScore(0);
          setTotalRiskScoreLevel('');
          setManualRiskScoreError('');
        }}
        title="Set Risk Score manually"
        sidepanelExpandedKey="manual-risk-score"
        errorSidepanel={manualRiskScoreError}
        footer={
          <>
            <ButtonPrimary
              text={createRiskScoreIsLoading ? 'Loading...' : 'Apply'}
              disabled={createRiskScoreIsLoading}
              onClick={createApplicationManualRiskScoreAction}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setManualScoreSidepanelVisible(false);
                setTotalRiskScore(0);
                setTotalRiskScoreLevel('');
                setManualRiskScoreError('');
              }}
              bold
            />
          </>
        }
      >
        <ManualRiskScoreForm
          totalRiskScoreLevel={totalRiskScoreLevel}
          riskScoreRanges={riskScoreRanges}
          setTotalRiskScoreLevel={setTotalRiskScoreLevel}
          setTotalRiskScore={setTotalRiskScore}
          totalRiskScore={totalRiskScore}
        />
      </SidePanel>
    </>
  );
};

export default NewApplicationTopSidepanels;
